import React, { useState } from 'react';
import { Form, Space, Modal, Upload, Button, Table, App, Typography } from 'antd';
import { UploadOutlined, DownloadOutlined } from '@ant-design/icons';
import * as XLSX from 'xlsx';
import { apiRequest } from '../../../../service/apiRequest';

const { Text } = Typography;

interface Props {
  visible: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const sampleTemplate = [
  {
    wood_name: 'Teak Wood',
    wood_thickness: 2,
    wood_lenth: 8,
    qty: 50,
  },
  {
    wood_name: 'Sheesham Wood',
    wood_thickness: 1.5,
    wood_lenth: 6,
    qty: 30,
  },
];

const ImportWoodModal: React.FC<Props> = ({ visible, onClose, onSuccess }) => {
  const { message } = App.useApp();
  const [parsedData, setParsedData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const downloadSample = () => {
    const ws = XLSX.utils.json_to_sheet(sampleTemplate);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'WoodTemplate');
    XLSX.writeFile(wb, 'wood_sample_template.xlsx');
  };

  const handleUpload = async (file: File) => {
    setLoading(true);
    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: 'array' });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const json = XLSX.utils.sheet_to_json(sheet, { defval: '' });

      if (!json.length) {
        message.warning('The Excel file is empty.');
        return false;
      }

      setParsedData(json);
    } catch (err) {
      console.error(err);
      message.error('Failed to read Excel file. Please check the format.');
    } finally {
      setLoading(false);
    }

    return false; // prevent auto-upload
  };

  const handleImport = async () => {
    if (!parsedData.length) return;
    setLoading(true);
    try {
      const res = await apiRequest<any>({
        method: 'post',
        url: '/import-woods',
        data: parsedData,
      });
      message.success(res?.message || 'Woods imported successfully!');
      setParsedData([]);
      onSuccess();
      onClose();
    } catch (err: any) {
      console.error(err);
      message.error(err?.response?.data?.message || 'Import failed. Please check the data format.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setParsedData([]);
    onClose();
  };

  return (
    <Modal
      title="Import Wood Master"
      width="70%"
      open={visible}
      onCancel={handleCancel}
      onOk={handleImport}
      okText="Import"
      okButtonProps={{ disabled: parsedData.length === 0 || loading, loading }}
      destroyOnHidden
    >
      <Form layout="vertical">
        <Form.Item label="Step 1: Download Sample Excel Template">
          <Space direction="vertical" size={4}>
            <Text type="secondary">
              Download the template with standard columns (wood_name, wood_thickness, wood_lenth, qty)
            </Text>
            <Button icon={<DownloadOutlined />} onClick={downloadSample}>
              Download Sample Template
            </Button>
          </Space>
        </Form.Item>

        <Form.Item label="Step 2: Upload Filled Excel File">
          <Space direction="vertical" size={4}>
            <Text type="secondary">Select your completed Excel file (.xlsx)</Text>
            <Upload
              accept=".xlsx,.xls"
              beforeUpload={handleUpload}
              showUploadList={false}
              disabled={loading}
            >
              <Button icon={<UploadOutlined />} loading={loading}>
                Upload Excel File
              </Button>
            </Upload>
          </Space>
        </Form.Item>
      </Form>

      {parsedData.length > 0 && (
        <>
          <Text style={{ marginTop: 12, display: 'block', fontWeight: 600 }}>
            Preview of Data to Import ({parsedData.length} items):
          </Text>
          <Table
            dataSource={parsedData}
            rowKey={(_, index) => index!.toString()}
            size="small"
            scroll={{ y: 240 }}
            pagination={false}
            columns={[
              {
                title: 'Wood Name (wood_name)',
                dataIndex: 'wood_name',
                key: 'wood_name',
                render: (val, record) => val || record['Wood Name'] || '-',
              },
              {
                title: 'Thickness (wood_thickness)',
                dataIndex: 'wood_thickness',
                key: 'wood_thickness',
                render: (val, record) => val ?? record['Wood Thickness'] ?? '-',
              },
              {
                title: 'Length (wood_lenth)',
                dataIndex: 'wood_lenth',
                key: 'wood_lenth',
                render: (val, record) => val ?? record['wood_length'] ?? record['Wood Length'] ?? '-',
              },
              {
                title: 'Qty (qty)',
                dataIndex: 'qty',
                key: 'qty',
                render: (val, record) => val ?? record['wood_qty'] ?? record['Qty'] ?? 0,
              },
            ]}
            style={{ marginTop: 8 }}
          />
        </>
      )}
    </Modal>
  );
};

export default ImportWoodModal;
